import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import useContactsEmployeeService from '../shared/services/contactsEmployeeService';
import { Show, useShowContext } from 'framework/show';
import ContactSection from '../shared/ContactSection';
import { EmployeePersonTile } from 'domain/shared/core/PersonTile';
import ManagersList from '../shared/ManagersList';
import { generateLinkByEmail } from 'framework/utils';
import useEmployeeNamesService from 'domain/shared/employee/employeeNames/employeeNamesService';

import { READ_EMPLOYEE_PERMISSION, useEmployeePermissionsMap } from 'domain/shared/employee';

const PeopleManagementSection = React.memo(() => {
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const { searchPeopleManagers } = useContactsEmployeeService();
  const { data: showData } = useShowContext();

  const hasSpecificManagers = showData?.getValueBySource('hasSpecificManagers');
  const assignedPeopleManagerAliases = useMemo(
    () => showData?.getValueBySource('assignedPeopleManagerAliases') || [],
    [showData],
  );
  const [managersData, setManagersData] = useState<any[]>([]);
  const managerAliases = managersData.map(m => m.alias);
  const { permissionsMap } = useEmployeePermissionsMap(managerAliases);

  useEffect(() => {
    const fetchManagersData = async () => {
      if (assignedPeopleManagerAliases.length > 0) {
        const managers = await getEmployeeNamesByAliases(assignedPeopleManagerAliases);
        setManagersData(managers);
      }
    };

    fetchManagersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedPeopleManagerAliases]);

  const renderManagersList = () => {
    if (hasSpecificManagers && managersData.length > 0) {
      return (
        <Box>
          {managersData.map(manager => {
            const isLink =
              Array.isArray(permissionsMap[manager.alias]) &&
              permissionsMap[manager.alias].includes(READ_EMPLOYEE_PERMISSION);

            return (
              <Box key={manager.alias} sx={{ mb: 2 }}>
                <EmployeePersonTile
                  employeeId={manager.alias}
                  title="People manager"
                  size="medium"
                  isLink={isLink}
                  showTeamsButton
                  teamsIconSize="medium"
                  teamsUrl={generateLinkByEmail(manager.workingEmail)}
                />
              </Box>
            );
          })}
        </Box>
      );
    }

    if (!hasSpecificManagers && assignedPeopleManagerAliases.length === 0) {
      return (
        <Show getDataMethod={searchPeopleManagers} isSmallLoader>
          <ManagersList />
        </Show>
      );
    }

    return <Typography color="textSecondary">People management team</Typography>;
  };

  const description = (
    <Box>
      {[
        'Career review and development',
        'Training, upskilling opportunities, and certification support',
        'Project assignments, workload, or role changes',
      ].map(text => (
        <Box key={text} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              aspectRatio: 1,
              bgcolor: 'success.main',
              mr: 1.5,
            }}
          />
          <Typography variant="body2">{text}</Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <ContactSection title="People management" subtitle="Issues people manager can help with" description={description}>
      {renderManagersList()}
    </ContactSection>
  );
});

export default PeopleManagementSection;
